import { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { requestAdminAccess, subscribeToOwnAdminRequest } from "../firebase/services";

// Shown in place of event-creation UI (Account.js's "YOUR EVENTS" tab,
// CreateEvent.js) for any signed-in user who isn't an approved admin. Only ever
// mounted by callers when isAdmin is false - it renders nothing once approval
// comes through, since the parent swaps back to the real UI on its own re-render.
export default function AdminAccessGate() {
  const { user, isAdmin, refreshClaims } = useAuth();
  const [request, setRequest] = useState(undefined); // undefined = loading, null = none yet
  // Callers only mount this once `user` has already loaded (behind ProtectedRoute
  // / an isAdmin check that itself depends on user), so this lazy initializer
  // reliably picks up their current display name as the form's default.
  const [name, setName] = useState(() => user?.displayName || "");
  const [affiliation, setAffiliation] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeToOwnAdminRequest(user.uid, setRequest);
    return unsub;
  }, [user]);

  useEffect(() => {
    if (request?.status === "approved" && !isAdmin) refreshClaims();
  }, [request, isAdmin, refreshClaims]);

  if (!user || request === undefined) return null;

  const canSubmit = name.trim() && reason.trim();

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    await requestAdminAccess(user.uid, user.email, { name: name.trim(), affiliation: affiliation.trim(), reason: reason.trim() });
    setSubmitting(false);
  };

  if (request && request.status === "pending") {
    return (
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-6 text-center">
        <div className="text-3xl mb-3">🔒</div>
        <p className="text-gray-400 text-sm mb-1">Admin request pending</p>
        <p className="text-gray-600 text-xs">The event owner needs to approve your request before you can create events.</p>
      </div>
    );
  }

  return (
    <div className="bg-dark-700 border border-dark-500 rounded-xl p-6">
      <div className="text-center mb-4">
        <div className="text-3xl mb-3">🔒</div>
        <p className="text-gray-400 text-sm mb-1">Only admins can create events</p>
        <p className="text-gray-600 text-xs">
          {request?.status === "denied"
            ? "Your previous request was denied. You can request again below."
            : "Fill this out and the event owner will review it."}
        </p>
      </div>
      <form onSubmit={handleRequest} className="flex flex-col gap-3">
        <div>
          <label htmlFor="admin-request-name" className="font-display text-xs font-bold text-gray-500 tracking-widest block mb-1.5">NAME</label>
          <input id="admin-request-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
            className="w-full p-3 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />
        </div>
        <div>
          <label htmlFor="admin-request-affiliation" className="font-display text-xs font-bold text-gray-500 tracking-widest block mb-1.5">AFFILIATION</label>
          <input id="admin-request-affiliation" value={affiliation} onChange={(e) => setAffiliation(e.target.value)} placeholder="School / club (optional)"
            className="w-full p-3 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />
        </div>
        <div>
          <label htmlFor="admin-request-reason" className="font-display text-xs font-bold text-gray-500 tracking-widest block mb-1.5">REASON FOR REQUEST</label>
          <textarea id="admin-request-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="What event(s) are you looking to run?"
            className="w-full p-3 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400 resize-none" />
        </div>
        <button type="submit" disabled={submitting || !canSubmit}
          className="w-full p-3 bg-gradient-to-br from-green-action-light to-green-action text-white font-display text-sm font-bold rounded-xl tracking-wide disabled:opacity-40 border-none cursor-pointer mt-1">
          {submitting ? "Requesting..." : request?.status === "denied" ? "Request Again" : "Request Admin Access"}
        </button>
      </form>
    </div>
  );
}
